'use client';

import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { Fail } from '@/app/components/icons/Fail';
import { Loading } from '@/app/components/icons/Loading';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TaskInfo } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { CancelButton } from '../../../_components/TaskCard/components/CancelButton';
import { TaskStatusAnimation } from '../../../_components/TaskCard/components/TaskStatusAnimation';
import { useTaskState } from '../../../_components/TaskCard/hooks/use-task-state';
import { getQueueI18nMessageConfig } from '../../../_components/TaskCard/utils';
import type { CompactTaskHistoryStateMessage } from '../../../_components/TaskHistory';

interface OfflineAvatarTaskStatePreviewProps {
  task: TaskInfo;
  stateMessage: CompactTaskHistoryStateMessage;
}

export const OfflineAvatarTaskStatePreview: FC<OfflineAvatarTaskStatePreviewProps> = ({ task, stateMessage }) => {
  const t = useTranslations();
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { userInfo } = useStore((state) => ({ userInfo: state.userInfo }));
  const { queueWaitTime, isWaitTimeProlonged, handleCancelTask } = useTaskState(task);
  const isQueueing = stateMessage.loading === 'queueing';
  const queueConfig = getQueueI18nMessageConfig(
    queueWaitTime?.min[0] || 0,
    queueWaitTime?.max[0] || 0,
    isWaitTimeProlonged,
  );

  return (
    <div className="relative size-full overflow-hidden rounded-8 bg-system-black">
      {isQueueing ? <TaskStatusAnimation /> : null}
      {stateMessage.loading === 'processing' ? (
        <div className="relative z-10 flex size-full flex-col items-center justify-center gap-4 px-8 py-10 text-center">
          <Loading className="animate-spin text-2xl text-system-white" />
          <p className="font-medium text-lg text-system-white">{t(stateMessage.titleKey)}</p>
        </div>
      ) : null}
      {!stateMessage.loading ? (
        <div className="relative z-10 flex size-full flex-col items-center justify-center gap-2 px-8 py-10 text-center text-sm text-system-white64">
          {stateMessage.icon === 'fail' ? <Fail className="shrink-0 text-2xl text-system-white64" /> : null}
          <p className="font-medium text-lg text-system-white">{t(stateMessage.titleKey)}</p>
          {stateMessage.contentKey ? <div className="max-w-[360px]">{t(stateMessage.contentKey)}</div> : null}
        </div>
      ) : null}
      {isQueueing ? (
        <div className="absolute right-8 bottom-[26px] left-8 z-10 flex flex-col items-start gap-3">
          <p className="font-medium text-lg text-system-white/70 leading-[26px]">{t(stateMessage.titleKey)}</p>
          {task.settings?.schedule_mode === 'nopeak' ? (
            <p className="text-left text-[#b3b6b8] text-sm leading-5">{t('task.status.queue.nopeak')}</p>
          ) : (
            <div className="text-left text-sm text-system-white/70 leading-[1.4]">
              {queueWaitTime?.max[0] ? (
                <p className="mb-1">
                  {t(
                    `task.status.queue.${queueConfig.key}`,
                    queueConfig.params
                      ? {
                          ...queueConfig.params,
                          unit: t(`task.status.queue.${queueConfig.params.unit}`),
                        }
                      : {},
                  )}
                </p>
              ) : null}
              {userInfo?.subs_plan === 'free' ? (
                <span className="inline-flex flex-wrap items-center gap-x-1">
                  {t.rich('task.status.queue.free', {
                    button: (chunks) => (
                      <button
                        type="button"
                        className="relative inline-flex cursor-pointer items-center focus-visible:outline-none"
                        onClick={() => {
                          openPlanDetailDialog({ fromPage: 'offline_avatar queueing upgrade button' });
                          reportEvent('vip_price_modal_exposure', {
                            from_page: 'offline_avatar queueing upgrade button',
                          });
                          sendGAEvent('event', 'subscription_exposure', {
                            from_page: 'offline_avatar queueing upgrade button',
                          });
                        }}
                      >
                        {chunks}
                      </button>
                    ),
                  })}
                </span>
              ) : (
                <span className="text-[#b3b6b8]">{t('task.status.queue.vip')}</span>
              )}
            </div>
          )}
        </div>
      ) : null}
      {isQueueing ? (
        <CancelButton
          className="absolute right-8 bottom-8 z-10 h-auto min-w-0 rounded-none border-0 bg-transparent p-0 font-normal text-system-white/40 text-xs leading-[1.4] enabled:hover:bg-transparent enabled:hover:text-system-white/60 disabled:text-system-white/30"
          taskId={task.id}
          onCancel={handleCancelTask}
        />
      ) : null}
    </div>
  );
};
